import { computed, Injectable, signal } from "@angular/core";
import { MessagingSdk } from "lib-klab-sdks";

import {
  CHAT_UPLOAD_ALLOWED_AUDIO_MIMES,
  CHAT_UPLOAD_ALLOWED_GENERAL_MIMES,
  CHAT_UPLOAD_BATCH_LIMIT,
  CHAT_UPLOAD_GENERAL_MAX_BYTES,
  CHAT_UPLOAD_GLOBAL_CONCURRENCY,
  CHAT_UPLOAD_PART_BYTES,
  CHAT_UPLOAD_SINGLE_MAX_BYTES,
  ChatUploadBubbleUI,
  ChatUploadMediaKind,
  ChatUploadMode,
} from "../../domain/models/messaging/chat-ui.model";
import { normalizeChatUploadMime } from "../../domain/models/messaging/chat-file-kind.util";
import {
  ChatUploadCompleteManifest,
  ChatUploadInitItem,
  ChatUploadPart,
  DocumentS3UploadService,
} from "./document-s3-upload.service";

export interface ChatUploadQueueFile {
  clientFileId: string;
  file: File;
  mediaKind: ChatUploadMediaKind;
  durationMs?: number | null;
  localObjectUrl?: string | null;
  /**
   * Id del mensaje optimista que representa este archivo en la conversación.
   * El manager no lo usa para subir: lo persiste para que, tras una recarga,
   * el mensaje se pueda reconstruir con su identidad original y la
   * reconciliación por clientMessageId siga funcionando.
   */
  clientMessageId: string;
  /** Caption ya recortado que acompaña al archivo, persistido junto con la sesión. */
  captionText: string | null;
  /** Metadata de álbum de imágenes (MOMSG-1172), persistida junto con la sesión para sobrevivir a una recarga a mitad de un envío. */
  album?: MessagingSdk.AlbumAssociation | null;
}

/**
 * Subida que quedó a medias cuando se recargó la página. NO es reanudable: el
 * navegador no puede persistir un handle de `File`, así que los bytes murieron
 * con la pestaña. Lleva solo lo necesario para reconstruir el mensaje como
 * placeholder descartable.
 */
export interface ChatInterruptedUpload {
  conversationId: string;
  clientFileId: string;
  clientMessageId: string;
  fileName: string;
  fileSize: number;
  declaredMime: string;
  mediaKind: ChatUploadMediaKind;
  durationMs: number | null;
  captionText: string | null;
  savedAt: number;
  /**
   * Porcentaje realmente subido antes del corte, derivado de las partes que S3
   * ya confirmó (`completedEtags` sobre `totalParts`). No se pierde al recargar:
   * esas partes siguen en S3. Sirve para que el placeholder muestre cuánto
   * había avanzado en vez de un 0% que no es cierto.
   */
  progress: number;
  /** Metadata de álbum de imágenes (MOMSG-1172), restaurada para que el placeholder rehidratado siga perteneciendo al álbum tras un reload. */
  album: MessagingSdk.AlbumAssociation | null;
}

export interface ChatUploadRejection {
  clientFileId: string;
  reason: string;
}

export interface ChatUploadEnqueueResult {
  accepted: ChatUploadBubbleUI[];
  rejected: ChatUploadRejection[];
}

const UPLOAD_STORAGE_KEY = 'klab.chat.upload.sessions';
const UPLOAD_SESSION_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/**
 * Margen con el que una URL presignada se da por vencida antes de tiempo. S3
 * valida la firma contra SU reloj, así que hay que dejar aire para el desfasaje
 * con el del navegador.
 */
const URL_REFRESH_MARGIN_MS = 2 * 60 * 1000;
/** Intentos por parte antes de dar el archivo por perdido. */
const PART_MAX_ATTEMPTS = 4;
/** Base del backoff. El primer reintento no espera: casi siempre alcanza con renovar la URL. */
const PART_RETRY_BASE_DELAY_MS = 500;

/**
 * URL presignada con su vencimiento ya resuelto a epoch ms.
 *
 * Antes solo se guardaba la cadena y el `expiresAt` que devuelve el servidor se
 * descartaba, así que la única forma de enterarse de que una firma había
 * vencido era gastar el PUT y comerse el 403. Con la fecha a mano el refresco
 * se puede pedir ANTES.
 */
interface PresignedPartUrl {
  url: string;
  /** `null` si el servidor no mandó una fecha parseable: sin fecha no hay refresco proactivo, solo el reactivo por error. */
  expiresAtMs: number | null;
}

function toPresignedPartUrl(source: { url: string; expiresAt: string }): PresignedPartUrl {
  const parsed = Date.parse(source.expiresAt);
  return { url: source.url, expiresAtMs: Number.isFinite(parsed) ? parsed : null };
}

interface UploadPersistedRecord {
  conversationId: string;
  clientFileId: string;
  fileName: string;
  fileSize: number;
  declaredMime: string;
  mediaKind: ChatUploadMediaKind;
  durationMs: number | null;
  sessionId: string;
  capability: string;
  partSize: number;
  totalParts: number;
  mode: ChatUploadMode;
  completedEtags: { partNumber: number; etag: string }[];
  savedAt: number;
  clientMessageId: string;
  captionText: string | null;
  /** Metadata de álbum de imágenes (MOMSG-1172), persistida junto con la sesión para sobrevivir a una recarga a mitad de un envío. */
  album?: MessagingSdk.AlbumAssociation | null;
}

interface ChatUploadJob {
  bubble: ChatUploadBubbleUI;
  file: File;
  /** Datos del mensaje optimista asociado; el manager solo los transporta hasta el registro persistido. */
  clientMessageId: string;
  captionText: string | null;
  /** Metadata de álbum de imágenes (MOMSG-1172); el manager solo la transporta hasta el registro persistido. */
  album: MessagingSdk.AlbumAssociation | null;
  sessionId?: string;
  capability?: string;
  partSize?: number;
  totalParts?: number;
  urlsByPart: Map<number, PresignedPartUrl>;
  /**
   * Bytes confirmados por parte. Las partes suben en paralelo, así que el
   * progreso NO puede ser `start + loaded` de la última parte que reportó:
   * la de mayor offset sola llevaría la barra al 100% mientras las anteriores
   * siguen subiendo. Guardando cada parte por separado, el progreso es la
   * suma, y también sobrevive al retry de una parte (se pisa su entrada, no
   * se acumula de más).
   */
  partLoaded: Map<number, number>;
  singleUrl?: PresignedPartUrl;
  /**
   * Refresco de URLs en vuelo. Las partes suben en paralelo y todas descubren
   * el vencimiento casi al mismo tiempo: sin esto cada una dispararía su propia
   * llamada de control (128 en un archivo de 2GB). La primera que se da cuenta
   * pide URLs frescas para TODO lo que falta y las demás se cuelgan de esa misma
   * promesa.
   */
  pendingRefresh?: Promise<void>;
  /**
   * Sube en cada `retry`. El intento anterior sigue desenrollándose en sus
   * propios microtasks: sin esto, su `catch` corre DESPUÉS de que `retry` ya
   * reseteó los flags y marca como fallido un job que ya volvió a arrancar.
   */
  generation: number;
  cancelled: boolean;
  settled: boolean;
  /**
   * Corta los PUT a S3 que este job tenga en vuelo. El flag `cancelled` solo se
   * consulta ENTRE awaits, así que por sí solo no detiene los bytes que ya
   * están viajando: sin este controller, cancelar dejaba subiendo hasta
   * CHAT_UPLOAD_GLOBAL_CONCURRENCY partes de 16MB (y el archivo entero en modo
   * SINGLE). Se reemplaza en cada `retry` porque un controller ya abortado lo
   * queda para siempre.
   */
  abort: AbortController;
}

@Injectable({ providedIn: "root" })
export class ChatUploadManager {
  private readonly bubbles = signal<Map<string, ChatUploadBubbleUI[]>>(
    new Map(),
  );
  private readonly interruptedBubbles = signal<Map<string, ChatUploadBubbleUI>>(new Map());
  /**
   * Registros restaurados de la sesión anterior, en orden de `savedAt`. Se
   * guardan aparte de las burbujas porque el consumidor que rehidrata mensajes
   * necesita campos (clientMessageId, captionText) que no pertenecen a
   * `ChatUploadBubbleUI` — la burbuja es para el tray, no para reconstruir un
   * mensaje.
   */
  private restoredRecords: ChatInterruptedUpload[] = [];
  private readonly jobs = new Map<string, ChatUploadJob>();
  private readonly settleWaiters = new Map<string, Array<() => void>>();
  /**
   * Cola FIFO por conversación. Los archivos se suben de a uno, en el orden en
   * que el usuario los seleccionó: si el primero es un video de 1GB y los que
   * siguen son fotos de 1KB, el video va primero igual. Antes cada archivo
   * corría en paralelo y el orden lo terminaba decidiendo el tamaño.
   *
   * La cola es POR CONVERSACIÓN, no global: este manager es un singleton root
   * y una cola única haría que un archivo pesado en un chat bloqueara las
   * subidas de otro chat. La concurrencia global de `withSlot` sigue viva y
   * ahora aplica a las PARTES de un mismo archivo y a conversaciones distintas.
   */
  private readonly conversationQueues = new Map<string, Promise<void>>();
  private active = 0;
  private readonly slotWaiters: Array<() => void> = [];

  readonly allActiveBubbles = computed<ChatUploadBubbleUI[]>(() => {
    const fromActive = Array.from(this.bubbles().values()).flat();
    const fromInterrupted = Array.from(this.interruptedBubbles().values());
    return [...fromActive, ...fromInterrupted];
  });

  constructor(private readonly uploadService: DocumentS3UploadService) {
    this.restoreFromStorage();
  }

  enqueue(
    conversationId: string,
    files: ChatUploadQueueFile[],
  ): ChatUploadEnqueueResult {
    const accepted: ChatUploadBubbleUI[] = [];
    const rejected: ChatUploadRejection[] = [];

    if (files.length > CHAT_UPLOAD_BATCH_LIMIT) {
      for (const file of files) {
        rejected.push({
          clientFileId: file.clientFileId,
          reason: `Batch exceeds the ${CHAT_UPLOAD_BATCH_LIMIT}-file limit.`,
        });
      }
      return { accepted, rejected };
    }

    for (const file of files) {
      const validationError = this.validate(file);
      if (validationError) {
        rejected.push({ clientFileId: file.clientFileId, reason: validationError });
        continue;
      }
      const bubble: ChatUploadBubbleUI = {
        conversationId,
        clientFileId: file.clientFileId,
        fileName: file.file.name,
        declaredMime: normalizeChatUploadMime(file.file.name, file.file.type),
        size: file.file.size,
        mediaKind: file.mediaKind,
        durationMs: file.durationMs ?? null,
        state: "pending",
        progress: 0,
        uploadedBytes: 0,
        error: null,
        reference: null,
        localObjectUrl: file.localObjectUrl ?? null,
      };
      const job: ChatUploadJob = {
        bubble,
        file: file.file,
        clientMessageId: file.clientMessageId,
        captionText: file.captionText,
        album: file.album ?? null,
        urlsByPart: new Map(),
        partLoaded: new Map(),
        generation: 0,
        cancelled: false,
        settled: false,
        abort: new AbortController(),
      };
      this.jobs.set(file.clientFileId, job);
      this.patchBubble(job, {});
      accepted.push(bubble);
    }

    if (accepted.length) {
      const batchJobs = accepted
        .map((bubble) => this.jobs.get(bubble.clientFileId))
        .filter((job): job is ChatUploadJob => !!job);
      this.chainUpload(conversationId, () => this.processBatch(batchJobs));
    }

    return { accepted, rejected };
  }

  /**
   * Encadena una tarea al final de la cola de su conversación. Un archivo que
   * falla nunca bloquea a los que siguen: el error se absorbe acá para que la
   * cola siga drenando (el estado de la burbuja ya lo marcó `fail`).
   */
  private chainUpload(conversationId: string, run: () => Promise<void>): void {
    const tail = this.conversationQueues.get(conversationId) ?? Promise.resolve();
    const next = tail.then(run).catch(() => undefined);
    this.conversationQueues.set(conversationId, next);
    void next.then(() => {
      // Si nadie encoló detrás, soltamos la entrada para no retener
      // conversaciones muertas en el mapa.
      if (this.conversationQueues.get(conversationId) === next) {
        this.conversationQueues.delete(conversationId);
      }
    });
  }

  cancel(clientFileId: string): void {
    // Un job VIVO gana siempre sobre un registro restaurado con el mismo
    // clientFileId. Antes se miraba primero `interruptedBubbles` y se retornaba
    // ahi: si `restoreFromStorage()` habia levantado una sesion vieja con ese
    // mismo id, el cancel del job real se perdia — la burbuja se quedaba en
    // 'uploading' y la sesion del servidor nunca se borraba.
    const job = this.jobs.get(clientFileId);
    if (job && !job.settled) {
      job.cancelled = true;
      // Primero cortar los bytes, después avisarle al servidor: si el orden se
      // invierte, las partes en vuelo siguen escribiendo en una sesión que el
      // backend ya está dando de baja.
      job.abort.abort();
      if (job.sessionId && job.capability) {
        void this.uploadService
          .deleteChatUpload(job.sessionId, job.capability)
          .catch(() => undefined);
      }
      this.patchBubble(job, { state: "cancelled" });
      this.settle(job);
      return;
    }
    if (this.interruptedBubbles().has(clientFileId)) {
      this.interruptedBubbles.update((m) => { const next = new Map(m); next.delete(clientFileId); return next; });
      this.restoredRecords = this.restoredRecords.filter((r) => r.clientFileId !== clientFileId);
      this.removePersistedRecord(clientFileId);
    }
  }

  retry(clientFileId: string): void {
    const job = this.jobs.get(clientFileId);
    if (!job || !job.settled) return;
    if (job.bubble.state !== "failed" && job.bubble.state !== "cancelled") {
      return;
    }
    job.cancelled = false;
    job.settled = false;
    job.generation++;
    // Un AbortController abortado queda abortado: reusarlo haría que el
    // reintento muriera al instante en el primer PUT.
    job.abort = new AbortController();
    job.sessionId = undefined;
    job.capability = undefined;
    job.urlsByPart.clear();
    job.partLoaded.clear();
    job.singleUrl = undefined;
    job.pendingRefresh = undefined;
    this.patchBubble(job, {
      state: "pending",
      progress: 0,
      uploadedBytes: 0,
      error: null,
    });
    // El reintento también respeta la cola: se pone al final, detrás de lo que
    // ya estuviera subiendo en esa conversación.
    this.chainUpload(job.bubble.conversationId, () => this.processBatch([job]));
  }

  /**
   * Subidas restauradas de una sesión anterior, listas para rehidratarse como
   * placeholders. El orden es el del almacenamiento, sin garantía: quien las
   * reconstruya y necesite un orden estable debe ordenarlas por `savedAt`.
   */
  interruptedUploads(): readonly ChatInterruptedUpload[] {
    return this.restoredRecords;
  }

  bubblesFor(conversationId: string): ChatUploadBubbleUI[] {
    return this.bubbles().get(conversationId) ?? [];
  }

  bubbleFor(clientFileId: string): ChatUploadBubbleUI | undefined {
    return this.jobs.get(clientFileId)?.bubble;
  }

  waitFor(clientFileId: string): Promise<void> {
    const job = this.jobs.get(clientFileId);
    if (!job || job.settled) return Promise.resolve();
    return new Promise((resolve) => {
      const waiters = this.settleWaiters.get(clientFileId) ?? [];
      waiters.push(resolve);
      this.settleWaiters.set(clientFileId, waiters);
    });
  }

  private async processBatch(jobs: ChatUploadJob[]): Promise<void> {
    // Un archivo cancelado mientras esperaba su turno no debe llegar a pedir
    // INIT: se saltea cuando le toca.
    const pending = jobs.filter((job) => !job.cancelled);
    if (!pending.length) return;

    const batchId = this.newBatchId();
    let items: ChatUploadInitItem[];
    try {
      // El INIT sigue yendo en lote (un solo round trip para toda la
      // selección); lo que se serializa después es la subida de los bytes.
      const response = await this.uploadService.initChatUpload({
        batchId,
        files: pending.map((job) => ({
          clientFileId: job.bubble.clientFileId,
          name: job.bubble.fileName,
          declaredMime: job.bubble.declaredMime,
          size: job.bubble.size,
          mediaKind: job.bubble.mediaKind,
          durationMs: job.bubble.durationMs ?? null,
        })),
      });
      items = response.items;
    } catch (error) {
      for (const job of pending) {
        this.fail(job, this.messageOf(error));
      }
      return;
    }

    const itemsById = new Map(items.map((item) => [item.clientFileId, item]));
    // Estrictamente secuencial y en orden de selección. `uploadOne` absorbe sus
    // propios errores (marca la burbuja como failed), así que un archivo roto
    // no corta el recorrido de los que siguen.
    for (const job of pending) {
      await this.uploadOne(job, itemsById.get(job.bubble.clientFileId));
    }
  }

  private async uploadOne(
    job: ChatUploadJob,
    item: ChatUploadInitItem | undefined,
  ): Promise<void> {
    const generation = job.generation;
    if (!item) {
      this.fail(job, "INIT did not return an upload for the file.", generation);
      return;
    }
    if (job.cancelled) return;
    job.sessionId = item.sessionId;
    job.capability = item.capability;
    job.partSize = item.partSize ?? CHAT_UPLOAD_PART_BYTES;
    job.totalParts = item.totalParts ?? 1;
    for (const url of item.urls) {
      if (url.partNumber != null) job.urlsByPart.set(url.partNumber, toPresignedPartUrl(url));
      else job.singleUrl = toPresignedPartUrl(url);
    }
    this.patchBubble(job, { state: "uploading" });
    this.persistJob(job, item.mode);
    try {
      if (item.mode === "SINGLE") {
        await this.uploadSingleMode(job, generation);
      } else {
        await this.uploadMultipartMode(job, generation);
      }
    } catch (error) {
      this.fail(job, this.messageOf(error), generation);
    }
  }

  /**
   * true cuando este intento ya no es el vigente: lo cancelaron, o el usuario
   * apretó Reintentar y hay otro corriendo. Un intento viejo que sigue vivo en
   * sus microtasks no debe subir bytes, completar, ni pisar estado.
   */
  private stale(job: ChatUploadJob, generation: number): boolean {
    return job.cancelled || job.generation !== generation;
  }

  private async uploadSingleMode(job: ChatUploadJob, generation: number): Promise<void> {
    if (!job.singleUrl) {
      throw new Error("INIT did not return a presigned URL for the file.");
    }
    // SINGLE uploads share the same global concurrency pool as multipart
    // parts (withSlot) — otherwise a batch of many small files (or several
    // batches across different conversations, since this manager is a root
    // singleton) would fire unlimited concurrent requests at once.
    const etag = await this.withSlot(() => this.uploadSingleWithRetry(job, generation));
    if (this.stale(job, generation)) return;
    await this.complete(job, { mode: "SINGLE", etag, parts: null });
  }

  private async uploadSingleWithRetry(job: ChatUploadJob, generation: number): Promise<string> {
    // SINGLE es el caso degenerado de una sola parte: usa el mismo mapa que
    // multipart para que patchProgress tenga una única forma de calcular.
    const onProgress = (loaded: number) => {
      job.partLoaded.set(1, loaded);
      this.patchProgress(job);
    };

    let lastError: unknown;
    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
      if (this.stale(job, generation)) return "";
      try {
        const url = await this.freshSingleUrl(job);
        return await this.uploadService.uploadSingle(url, job.file, onProgress, job.abort.signal);
      } catch (error) {
        if (this.stale(job, generation)) throw error;
        lastError = error;
        job.partLoaded.set(1, 0);
        this.patchProgress(job);
        job.singleUrl = undefined;
        if (attempt < PART_MAX_ATTEMPTS) await this.backoff(attempt);
      }
    }
    throw lastError;
  }

  private async uploadMultipartMode(job: ChatUploadJob, generation: number): Promise<void> {
    const totalParts = job.totalParts ?? 1;
    const partSize = job.partSize ?? CHAT_UPLOAD_PART_BYTES;
    const etags = new Map<number, string>();
    const uploaded = new Set<number>();

    try {
      const response = await this.uploadService.listChatUploadParts(
        job.sessionId!,
        job.capability!,
      );
      for (const part of response.parts ?? []) {
        uploaded.add(part.partNumber);
        etags.set(part.partNumber, part.etag);
        // Las partes que el servidor ya tiene cuentan para el progreso: si no,
        // una subida reanudada arranca visualmente en 0% aunque la mayor parte
        // del archivo ya esté en S3.
        job.partLoaded.set(part.partNumber, this.partBytes(job, part.partNumber));
      }
      this.patchProgress(job);
    } catch {
      // ListParts is best-effort: without it we simply upload every part.
    }

    const missingParts: number[] = [];
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (!uploaded.has(partNumber)) missingParts.push(partNumber);
    }

    // Lo que todavía falta subir, evaluado en el momento: es el lote que se pide
    // cuando hay que renovar firmas, para no gastar una llamada por parte.
    const remaining = () => missingParts.filter((partNumber) => !etags.has(partNumber));

    await Promise.all(
      missingParts.map((partNumber) =>
        this.withSlot(() =>
          this.uploadPartWithRetry(job, partNumber, partSize, etags, remaining, generation),
        ),
      ),
    );

    if (this.stale(job, generation)) return;
    // La última parte casi nunca mide partSize completo (el tamaño del
    // archivo raramente es múltiplo exacto de 16MB): hay que recortarla al
    // tamaño real, igual que uploadPartWithRetry hace al subir los bytes.
    // Declarar partSize fijo para todas las partes hace que la suma de
    // tamaños no coincida con el tamaño real del archivo y el backend
    // rechaza el complete con UPLOAD_INVALID_REQUEST (400).
    const parts: ChatUploadPart[] = Array.from(etags.entries())
      .sort(([a], [b]) => a - b)
      .map(([partNumber, etag]) => {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(partNumber * partSize, job.file.size);
        return { partNumber, etag, size: end - start };
      });
    await this.complete(job, { mode: "MULTIPART", etag: null, parts });
  }

  /**
   * Sube una parte, renovando la firma cuando hace falta y reintentando un
   * número acotado de veces.
   *
   * Antes había UN solo reintento: cualquier segundo tropiezo — un 5xx de S3,
   * un corte de red, un refresco que no llegó — hacía rechazar el `Promise.all`
   * y con eso moría el archivo completo. En un 2GB son 128 partes, o sea 128
   * oportunidades de perder media hora de subida por un hipo de un segundo.
   */
  private async uploadPartWithRetry(
    job: ChatUploadJob,
    partNumber: number,
    partSize: number,
    etags: Map<number, string>,
    remaining: () => number[],
    generation: number,
  ): Promise<void> {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(partNumber * partSize, job.file.size);
    const onProgress = (loaded: number) => {
      job.partLoaded.set(partNumber, loaded);
      this.patchProgress(job);
    };

    let lastError: unknown;
    for (let attempt = 1; attempt <= PART_MAX_ATTEMPTS; attempt++) {
      if (this.stale(job, generation)) return;
      try {
        const url = await this.freshPartUrl(job, partNumber, remaining);
        const etag = await this.uploadService.uploadPart(url, job.file, start, end, onProgress, job.abort.signal);
        etags.set(partNumber, etag);
        // El último onprogress del XHR no siempre reporta el total exacto; al
        // confirmarse la parte se fija en su tamaño real para que la suma cierre.
        job.partLoaded.set(partNumber, end - start);
        this.patchProgress(job);
        this.updatePersistedEtag(job.bubble.clientFileId, partNumber, etag);
        return;
      } catch (error) {
        if (this.stale(job, generation)) throw error;
        lastError = error;
        // Un PUT cortado a medias dejó bytes contados que nunca llegaron: sin
        // esto la barra queda inflada por lo que se subió y se perdió.
        job.partLoaded.set(partNumber, 0);
        this.patchProgress(job);
        // La firma que se usó ya no sirve; el próximo intento pide una nueva.
        job.urlsByPart.delete(partNumber);
        if (attempt < PART_MAX_ATTEMPTS) await this.backoff(attempt);
      }
    }
    throw lastError;
  }

  /** URL vigente de una parte, renovándola por lote si venció o está por vencer. */
  private async freshPartUrl(
    job: ChatUploadJob,
    partNumber: number,
    remaining: () => number[],
  ): Promise<string> {
    const current = job.urlsByPart.get(partNumber);
    if (current && !this.isExpiring(current)) return current.url;

    try {
      await this.refreshUrls(job, remaining);
    } catch (error) {
      // Si la que hay todavía no venció del todo, se intenta con esa antes de
      // dar la parte por perdida: el refresco se reintenta en la vuelta siguiente.
      if (current && !this.isExpired(current)) return current.url;
      throw error;
    }

    const refreshed = job.urlsByPart.get(partNumber);
    if (!refreshed) {
      throw new Error(`Upload URL refresh did not return part ${partNumber}.`);
    }
    return refreshed.url;
  }

  private async freshSingleUrl(job: ChatUploadJob): Promise<string> {
    const current = job.singleUrl;
    if (current && !this.isExpiring(current)) return current.url;

    try {
      await this.refreshUrls(job, () => []);
    } catch (error) {
      if (current && !this.isExpired(current)) return current.url;
      throw error;
    }

    if (!job.singleUrl) {
      throw new Error("Upload URL refresh did not return the file URL.");
    }
    return job.singleUrl.url;
  }

  /**
   * Pide firmas frescas para `partNumbers`, uniendo en una sola llamada a todas
   * las partes que la necesiten al mismo tiempo.
   */
  private refreshUrls(job: ChatUploadJob, remaining: () => number[]): Promise<void> {
    if (job.pendingRefresh) return job.pendingRefresh;

    const run = this.runRefresh(job, remaining());
    job.pendingRefresh = run;
    void run
      .catch(() => undefined)
      .then(() => {
        if (job.pendingRefresh === run) job.pendingRefresh = undefined;
      });
    return run;
  }

  private async runRefresh(job: ChatUploadJob, partNumbers: number[]): Promise<void> {
    const response = await this.uploadService.refreshChatUploadUrls(
      job.sessionId!,
      job.capability!,
      partNumbers,
    );
    for (const item of response.urls) {
      if (item.partNumber != null) job.urlsByPart.set(item.partNumber, toPresignedPartUrl(item));
      else job.singleUrl = toPresignedPartUrl(item);
    }
  }

  private isExpiring(entry: PresignedPartUrl): boolean {
    return entry.expiresAtMs != null && entry.expiresAtMs - Date.now() <= URL_REFRESH_MARGIN_MS;
  }

  private isExpired(entry: PresignedPartUrl): boolean {
    return entry.expiresAtMs != null && entry.expiresAtMs <= Date.now();
  }

  /** El primer reintento sale sin esperar — suele bastar con renovar la firma. */
  private backoff(attempt: number): Promise<void> {
    if (attempt <= 1) return Promise.resolve();
    const delay = PART_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 2);
    return new Promise((resolve) => setTimeout(resolve, delay));
  }

  private async complete(
    job: ChatUploadJob,
    manifest: ChatUploadCompleteManifest,
  ): Promise<void> {
    if (job.cancelled || !job.sessionId || !job.capability) return;
    const response = await this.uploadService.completeChatUpload(
      job.sessionId,
      job.capability,
      manifest,
    );
    if (job.cancelled) return;
    this.patchBubble(job, {
      state: "completed",
      progress: 100,
      uploadedBytes: job.bubble.size,
      error: null,
      reference: response.reference,
      receipt: response.receipt ?? null,
    });
    this.settle(job);
  }

  /**
   * `generation` es el intento que produjo el error. Si el job ya arrancó otro
   * (el usuario apretó Reintentar mientras el anterior se desenrollaba), este
   * fallo es de una carrera vieja y no debe pisar el estado del intento vivo.
   */
  private fail(job: ChatUploadJob, message: string, generation?: number): void {
    if (job.cancelled || job.settled) return;
    if (generation != null && generation !== job.generation) return;
    this.patchBubble(job, { state: "failed", error: message });
    this.settle(job);
  }

  private settle(job: ChatUploadJob): void {
    if (job.settled) return;
    if (job.bubble.localObjectUrl) {
      try { URL.revokeObjectURL(job.bubble.localObjectUrl); } catch { /* ignore */ }
      this.patchBubble(job, { localObjectUrl: null });
    }
    job.settled = true;
    this.removePersistedRecord(job.bubble.clientFileId);
    const waiters = this.settleWaiters.get(job.bubble.clientFileId);
    if (waiters) {
      this.settleWaiters.delete(job.bubble.clientFileId);
      for (const resolve of waiters) resolve();
    }
  }

  private withSlot<T>(fn: () => Promise<T>): Promise<T> {
    if (this.active >= CHAT_UPLOAD_GLOBAL_CONCURRENCY) {
      return new Promise<T>((resolve, reject) => {
        this.slotWaiters.push(() => {
          this.runInSlot(fn).then(resolve, reject);
        });
      });
    }
    return this.runInSlot(fn);
  }

  private async runInSlot<T>(fn: () => Promise<T>): Promise<T> {
    this.active++;
    try {
      return await fn();
    } finally {
      this.active--;
      this.slotWaiters.shift()?.();
    }
  }

  /** Bytes reales de una parte, recortando la última al tamaño del archivo. */
  private partBytes(job: ChatUploadJob, partNumber: number): number {
    const partSize = job.partSize ?? CHAT_UPLOAD_PART_BYTES;
    const start = (partNumber - 1) * partSize;
    const end = Math.min(partNumber * partSize, job.file.size);
    return Math.max(0, end - start);
  }

  private patchProgress(job: ChatUploadJob): void {
    const size = job.bubble.size;
    let uploadedBytes = 0;
    for (const loaded of job.partLoaded.values()) uploadedBytes += loaded;
    uploadedBytes = Math.min(uploadedBytes, size);
    const progress =
      size > 0 ? Math.min(100, Math.round((uploadedBytes / size) * 100)) : 0;
    this.patchBubble(job, { uploadedBytes, progress });
  }

  private patchBubble(
    job: ChatUploadJob,
    patch: Partial<ChatUploadBubbleUI>,
  ): void {
    const next = { ...job.bubble, ...patch };
    job.bubble = next;
    this.bubbles.update((current) => {
      const result = new Map(current);
      const list = [...(result.get(next.conversationId) ?? [])];
      const index = list.findIndex(
        (bubble) => bubble.clientFileId === next.clientFileId,
      );
      if (index === -1) list.push(next);
      else list[index] = next;
      result.set(next.conversationId, list);
      return result;
    });
  }

  private persistJob(job: ChatUploadJob, mode: ChatUploadMode): void {
    try {
      const records = this.readPersistedRecords();
      const idx = records.findIndex((r) => r.clientFileId === job.bubble.clientFileId);
      const record: UploadPersistedRecord = {
        conversationId: job.bubble.conversationId,
        clientFileId: job.bubble.clientFileId,
        fileName: job.bubble.fileName,
        fileSize: job.bubble.size,
        declaredMime: job.bubble.declaredMime,
        mediaKind: job.bubble.mediaKind,
        durationMs: job.bubble.durationMs ?? null,
        sessionId: job.sessionId!,
        capability: job.capability!,
        partSize: job.partSize!,
        totalParts: job.totalParts!,
        mode,
        completedEtags: [],
        savedAt: Date.now(),
        clientMessageId: job.clientMessageId,
        captionText: job.captionText,
        album: job.album,
      };
      if (idx >= 0) records[idx] = record;
      else records.push(record);
      this.writePersistedRecords(records);
    } catch { /* localStorage unavailable — non-fatal */ }
  }

  private updatePersistedEtag(clientFileId: string, partNumber: number, etag: string): void {
    try {
      const records = this.readPersistedRecords();
      const record = records.find((r) => r.clientFileId === clientFileId);
      if (!record) return;
      const idx = record.completedEtags.findIndex((e) => e.partNumber === partNumber);
      if (idx >= 0) record.completedEtags[idx] = { partNumber, etag };
      else record.completedEtags.push({ partNumber, etag });
      this.writePersistedRecords(records);
    } catch { /* non-fatal */ }
  }

  private removePersistedRecord(clientFileId: string): void {
    try {
      this.writePersistedRecords(
        this.readPersistedRecords().filter((r) => r.clientFileId !== clientFileId),
      );
    } catch { /* non-fatal */ }
  }

  private readPersistedRecords(): UploadPersistedRecord[] {
    try {
      const raw = localStorage.getItem(UPLOAD_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private writePersistedRecords(records: UploadPersistedRecord[]): void {
    localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify(records));
  }

  private restoreFromStorage(): void {
    const now = Date.now();
    const all = this.readPersistedRecords();
    const valid = all.filter((r) => now - r.savedAt < UPLOAD_SESSION_MAX_AGE_MS);
    if (valid.length !== all.length) {
      this.writePersistedRecords(valid);
    }
    if (valid.length === 0) return;

    const interrupted = new Map<string, ChatUploadBubbleUI>();
    const rehydratable: ChatInterruptedUpload[] = [];
    for (const record of valid) {
      const progress = record.totalParts > 0
        ? Math.round((record.completedEtags.length / record.totalParts) * 100)
        : 0;
      // Un registro escrito por un build anterior no trae clientMessageId: la
      // burbuja del tray sigue siendo válida, pero sin ese id no se puede
      // reconstruir el mensaje (ni reconciliarlo si el real llega después), así
      // que se omite de las subidas rehidratables en vez de romper.
      if (typeof record.clientMessageId === 'string' && record.clientMessageId.length > 0) {
        rehydratable.push({
          conversationId: record.conversationId,
          clientFileId: record.clientFileId,
          clientMessageId: record.clientMessageId,
          fileName: record.fileName,
          fileSize: record.fileSize,
          declaredMime: record.declaredMime,
          mediaKind: record.mediaKind,
          durationMs: record.durationMs,
          captionText: typeof record.captionText === 'string' ? record.captionText : null,
          savedAt: record.savedAt,
          progress,
          album: record.album ?? null,
        });
      }
      interrupted.set(record.clientFileId, {
        conversationId: record.conversationId,
        clientFileId: record.clientFileId,
        fileName: record.fileName,
        declaredMime: record.declaredMime,
        size: record.fileSize,
        mediaKind: record.mediaKind,
        durationMs: record.durationMs,
        state: 'failed',
        progress,
        uploadedBytes: 0,
        error: 'Upload was interrupted.',
        reference: null,
        localObjectUrl: null,
      });
    }
    this.restoredRecords = rehydratable;
    this.interruptedBubbles.set(interrupted);
  }

  private validate(file: ChatUploadQueueFile): string | null {
    if (file.file.size <= 0) {
      return "Empty files are not supported.";
    }
    if (file.mediaKind === "GENERAL" && file.file.size > CHAT_UPLOAD_GENERAL_MAX_BYTES) {
      return `GENERAL files must be at most ${CHAT_UPLOAD_GENERAL_MAX_BYTES} bytes.`;
    }
    if (file.mediaKind === "ATTACHED_AUDIO" && file.file.size > CHAT_UPLOAD_SINGLE_MAX_BYTES) {
      return `Attached audio must be at most ${CHAT_UPLOAD_SINGLE_MAX_BYTES} bytes.`;
    }
    const declaredMime = normalizeChatUploadMime(file.file.name, file.file.type);
    if (file.mediaKind === "GENERAL" && !CHAT_UPLOAD_ALLOWED_GENERAL_MIMES.has(declaredMime)) {
      return `File type "${declaredMime}" is not supported.`;
    }
    if (file.mediaKind === "ATTACHED_AUDIO" && !CHAT_UPLOAD_ALLOWED_AUDIO_MIMES.has(declaredMime)) {
      return `Audio type "${declaredMime}" is not supported.`;
    }
    return null;
  }

  private newBatchId(): string {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `batch-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private messageOf(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }
}
